import { motion } from '@/lib/motion';
import { Brain } from 'lucide-react';

import { ChatMock } from '@/components/marketing/chat-mock';
import { HeroCallCard } from '@/components/marketing/hero-call-card';

/**
 * The home hero's signature asset: a live phone call (left) and a live website
 * chat (right), both answered from the same knowledge core glyph in the middle.
 * This single visual *is* the pitch — one brain, every channel. Both inner
 * mocks honor prefers-reduced-motion on their own.
 */
export function DualChannelHero(): JSX.Element {
  return (
    <div className="relative mx-auto max-w-5xl">
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto_1fr]">
        {/* Voice */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
        >
          <ChannelLabel tone="accent" text="On the phone" />
          <HeroCallCard />
        </motion.div>

        {/* Shared brain */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="relative z-10 mx-auto flex flex-col items-center"
        >
          {/* Connector lines on desktop */}
          <span className="pointer-events-none absolute right-full top-1/2 hidden h-px w-6 -translate-y-1/2 bg-gradient-to-r from-transparent to-[var(--brand-accent)]/60 lg:block" />
          <span className="pointer-events-none absolute left-full top-1/2 hidden h-px w-6 -translate-y-1/2 bg-gradient-to-l from-transparent to-emerald-500/60 lg:block" />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[var(--brand-accent)] to-emerald-500 text-white shadow-xl">
            <span className="absolute inset-0 rounded-2xl bg-[var(--brand-accent)]/40 blur-lg" />
            <Brain className="relative h-8 w-8" />
          </div>
          <span className="mt-2 hidden whitespace-nowrap text-[11px] font-medium text-muted-foreground lg:block">
            One knowledge base
          </span>
        </motion.div>

        {/* Chat */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <ChannelLabel tone="emerald" text="On your website" />
          <ChatMock />
        </motion.div>
      </div>
    </div>
  );
}

function ChannelLabel({
  tone,
  text,
}: {
  tone: 'accent' | 'emerald';
  text: string;
}): JSX.Element {
  const cls =
    tone === 'accent'
      ? 'bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]'
      : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
  return (
    <div className="mb-3 flex justify-center">
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>{text}</span>
    </div>
  );
}
